/**
 * „Zurück auf Anfrage" mit Rückfrage (Benjamin, 12.10.2026): nicht jedes Projekt,
 * das als „zu planen" auftaucht, ist wirklich beauftragt — oft ist das Angebot
 * noch gar nicht bestätigt. Den Status allein zurückzusetzen hielt nicht, weil die
 * Phase aus dem angenommenen Angebot gleich wieder „aktiv" rechnete. Deshalb
 * nimmt dieser Dialog die Annahme zurück und fragt, was mit den offenen
 * Aufgaben am Projekt passieren soll.
 *
 * Termine zählen für die Phase als Einsatz (13.10.2026, Treppenbau Athenstaedt: eine
 * „Besprechung" als gewöhnlicher Termin hielt die Phase auf „Abrechnen?", der Status
 * sprang sofort zurück). Hält die Phase deshalb, nennt der Dialog die Termine; was
 * kein Einsatz war, wird zur Besichtigung und zählt nicht mehr.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { fehlerText } from "@vimaana/ui";

import { useProjektZurueck, useZurueckVorschau } from "../../hooks/useProjektZurueck";

const eur = (n: number) => n.toLocaleString("de-DE", { style: "currency", currency: "EUR" });

type Props = { projektId: string; titel?: string; onClose: () => void; onFertig?: (text: string) => void };

export default function StatusZurueckDialog({ projektId, titel, onClose, onFertig }: Props) {
  const v = useZurueckVorschau(projektId);
  const zurueck = useProjektZurueck();
  const [angebote, setAngebote] = useState<Set<string>>(new Set());
  const [ablegen, setAblegen] = useState<Set<string>>(new Set());
  const [keinEinsatz, setKeinEinsatz] = useState<Set<string>>(new Set());

  // Angebote standardmäßig zurücksetzen — das ist der Kern der Sache. Termine, die nach
  // Besprechung/Besichtigung klingen, sind als „kein Einsatz" vorgewählt.
  useEffect(() => {
    if (!v.data) return;
    setAngebote(new Set(v.data.angebote.map((a) => a.id)));
    setKeinEinsatz(new Set(v.data.termine.filter((t) => t.kein_einsatz).map((t) => t.id)));
  }, [v.data]);
  const alleKeinEinsatz = !!v.data && v.data.termine.length > 0 && v.data.termine.every((t) => keinEinsatz.has(t.id));
  const danach = v.data ? (alleKeinEinsatz ? v.data.phase_ohne_termine : v.data.phase_danach) : null;
  const haeltNicht = !!v.data && (alleKeinEinsatz ? v.data.phase_ohne_termine.bleibt : !!v.data.warnung);

  const umschalten = (menge: Set<string>, setzen: (s: Set<string>) => void, id: string) => {
    const neu = new Set(menge);
    if (neu.has(id)) neu.delete(id); else neu.add(id);
    setzen(neu);
  };
  const eigene = v.data?.aufgaben.filter((a) => a.art === "eigene") ?? [];
  const automatisch = v.data?.aufgaben.filter((a) => a.art === "automatisch") ?? [];

  const ausfuehren = () => zurueck.mutate(
    { projektId, angebote_zuruecksetzen: [...angebote], aufgaben_ablegen: [...ablegen], termine_kein_einsatz: [...keinEinsatz] },
    {
      onSuccess: (r) => {
        const teile = [r.warnung ? "Status gesetzt — aber die Phase zieht ihn wieder vor." : "Zurück auf Anfrage."];
        if (r.angebote_zurueckgesetzt) teile.push(`${r.angebote_zurueckgesetzt} Angebot(e) wieder „gestellt“.`);
        if (r.termine_kein_einsatz) teile.push(`${r.termine_kein_einsatz} Termin(e) als Besichtigung geführt.`);
        if (r.aufgaben_abgelegt) teile.push(`${r.aufgaben_abgelegt} Aufgabe(n) abgelegt.`);
        onFertig?.(teile.join(" "));
        onClose();
      },
    },
  );

  return createPortal(
    <div className="vg-overlay" role="dialog" aria-modal="true" aria-label="Zurück auf Anfrage">
      <button type="button" className="vg-overlay-backdrop" aria-label="Schließen" onClick={onClose} />
      <div className="vg-overlay-panel">
        <div className="vg-overlay-head">
          <h2 className="vg-overlay-title">Zurück auf Anfrage{titel ? ` — ${titel}` : ""}</h2>
          <button type="button" className="vg-overlay-close" onClick={onClose} aria-label="Schließen">×</button>
        </div>
        {v.isLoading && <p className="pk-meta">Lade …</p>}
        {v.error && <div className="vg-banner is-warn">{fehlerText(v.error)}</div>}
        {v.data && (
          <>
            <p className="pk-meta">
              Das Projekt gilt danach als „Angebot beim Kunden“, nicht mehr als beauftragt.
            </p>
            {v.data.angebote.length > 0 && (
              <fieldset className="sz-gruppe">
                <legend>Angebote — wieder auf „gestellt“</legend>
                {v.data.angebote.map((a) => (
                  <label key={a.id} className="sz-zeile">
                    <input type="checkbox" checked={angebote.has(a.id)} onChange={() => umschalten(angebote, setAngebote, a.id)} />
                    <span>{a.nummer} · {eur(a.brutto)}</span>
                  </label>
                ))}
              </fieldset>
            )}
            {eigene.length > 0 && (
              <fieldset className="sz-gruppe">
                <legend>Deine Aufgaben am Projekt — was soll damit passieren?</legend>
                {eigene.map((a) => (
                  <div key={a.id} className="sz-zeile sz-wahl">
                    <span className="sz-text">{a.text}</span>
                    <span className="sz-knoepfe" role="radiogroup" aria-label={`Aufgabe: ${a.text}`}>
                      <button type="button" role="radio" aria-checked={!ablegen.has(a.id)}
                        className={"vg-btn" + (!ablegen.has(a.id) ? " vg-btn-primary" : "")}
                        onClick={() => { const n = new Set(ablegen); n.delete(a.id); setAblegen(n); }}>Behalten</button>
                      <button type="button" role="radio" aria-checked={ablegen.has(a.id)}
                        className={"vg-btn" + (ablegen.has(a.id) ? " vg-btn-primary" : "")}
                        onClick={() => { const n = new Set(ablegen); n.add(a.id); setAblegen(n); }}>Ablegen</button>
                    </span>
                  </div>
                ))}
              </fieldset>
            )}
            {automatisch.length > 0 && (
              <fieldset className="sz-gruppe">
                <legend>Automatische Aufgaben — regelt Kontor selbst</legend>
                {automatisch.map((a) => (
                  <div key={a.id} className="sz-zeile"><span className="sz-text">{a.text}</span><small className="pk-meta">{a.hinweis}</small></div>
                ))}
              </fieldset>
            )}
            {v.data.aufgaben.length === 0 && <p className="pk-meta">Keine offenen Aufgaben am Projekt.</p>}
            {v.data.termine.length > 0 && (
              <fieldset className="sz-gruppe">
                <legend>Termine — diese zählen als Einsatz. Was war keiner?</legend>
                {v.data.termine.map((t) => (
                  <label key={t.id} className="sz-zeile">
                    <input type="checkbox" checked={keinEinsatz.has(t.id)} onChange={() => umschalten(keinEinsatz, setKeinEinsatz, t.id)} />
                    <span className="sz-text">{new Date(t.datum).toLocaleDateString("de-DE")} · {t.titel}</span>
                    <small className="pk-meta">kein Einsatz → als Besichtigung führen</small>
                  </label>
                ))}
              </fieldset>
            )}
            {danach && <p className="pk-meta">Danach: {danach.name} — {danach.grund}</p>}
            {haeltNicht && (
              <div className="vg-banner is-warn">
                {v.data.termine.length > 0 && !alleKeinEinsatz
                  ? "Solange ein Termin als Einsatz zählt, rechnet die Phase „beauftragt“ — der Status würde gleich wieder vorgezogen."
                  : v.data.warnung || `Die Phase bliebe „${danach?.name}“ — der Status würde gleich wieder vorgezogen.`}
              </div>
            )}
          </>
        )}
        {zurueck.error && <div className="vg-banner is-warn">{fehlerText(zurueck.error)}</div>}
        <div className="vg-overlay-actions">
          <button type="button" className="vg-btn vg-btn-ghost" onClick={onClose}>Abbrechen</button>
          <button type="button" className="vg-btn vg-btn-primary" disabled={!v.data || zurueck.isPending} onClick={ausfuehren}>
            {zurueck.isPending ? "…" : "Zurückstellen"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
